import { type FormEventHandler, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Pillow } from '@/components/Pillow/Pillow'
import { ScrollPast } from '@/components/ProductPage/ScrollPast/ScrollPast'
import { useInboundSalesOpen } from '@/utils/useInboundSalesOpen'
import { Button } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { ProductSelector } from './ProductSelector/ProductSelector'
import { SsnField } from './SsnField/SsnField'
import { inboundSalesDialogWindow, ribbon, stickyButton, stickyButtonWrapper } from './ProductPurchaseForm.css'

export const SSN_FIELDNAME = 'ssn'
export const PRODUCT_FIELDNAME = 'product'

export type ProductOption = {
  name: string
  value: string
  img: {
    src: string
    alt?: string
  }
}

export type FormError = {
  general?: string
  ssn?: string
}

export type ProductPurchaseFormProps = {
  productOptions: Array<ProductOption>
  onSubmit: FormEventHandler<HTMLFormElement>
  productByline?: string
  defaultValue?: ProductOption['value']
  submitting?: boolean
  ssnDefaultValue?: string
  showHeader?: boolean
  error?: FormError
  ribbonText?: string
  promoteInboundSales?: boolean
}

export const ProductPurchaseForm = ({
  productOptions,
  onSubmit,
  productByline,
  defaultValue,
  submitting = false,
  ssnDefaultValue = '',
  showHeader,
  error,
  ribbonText,
  promoteInboundSales = false,
}: ProductPurchaseFormProps) => {
  const { t } = useTranslation('purchase-form')
  const cardRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLFormElement>(null)

  // When clicking the sticky button, we want to submit the form if it is valid,
  // otherwise scroll back up to the form so the user can complete it.
  // Using type="submit" + "form" attribute on the sticky button is a suboptimal solution because:
  //  1. Smooth scrolling is disabled globally and we can't option into it here while
  //     using native "scroll into view on form submission" feature.
  //  2. Getting the whole form into view at the center of the viewport is way easier by using
  //     scrollIntoView API.
  const handleStickyButtonClick = useCallback(() => {
    const form = formRef.current
    if (!form) return

    if (form.reportValidity()) {
      form.requestSubmit()
    } else {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      form.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' })
    }
  }, [])

  return (
    <form ref={formRef} onSubmit={onSubmit}>
      <Card.Root ref={cardRef}>
        {ribbonText && (
          <Card.Ribbon className={ribbon}>
            <Text as="p" size="xs" color="signalGreenText" align="center" singleLine>
              {ribbonText}
            </Text>
          </Card.Ribbon>
        )}
        <div className={yStack({ gap: 'xs' })}>
          {productOptions.length === 1 && (
            <>
              <input type="hidden" name={PRODUCT_FIELDNAME} value={productOptions[0].value} />

              {showHeader && (
                <Card.Header className={sprinkles({ marginBottom: 'xs' })}>
                  <Pillow size={{ _: 'small' }} {...productOptions[0].img} imagePriority={true} />
                  <div>
                    <Text>{productOptions[0].name}</Text>
                    {productByline && <Text color="textSecondary">{productByline}</Text>}
                  </div>
                </Card.Header>
              )}
            </>
          )}

          {productOptions.length > 1 && (
            <ProductSelector
              productOptions={productOptions}
              defaultValue={defaultValue}
              name={PRODUCT_FIELDNAME}
              disabled={submitting}
              required={true}
            />
          )}

          <div className={yStack({ gap: 'xs' })}>
            <SsnField
              name={SSN_FIELDNAME}
              defaultValue={ssnDefaultValue}
              required={true}
              disabled={submitting}
              warning={!!error?.ssn}
              message={error?.ssn}
            />

            <Button type="submit" size="large" loading={submitting} fullWidth={true}>
              {t('BUTTON_LABEL_GET_PRICE')}
            </Button>
          </div>

          <ScrollPast targetRef={cardRef}>
            <div className={stickyButtonWrapper}>
              {promoteInboundSales && <InboundSalesDialog />}
              <Button
                type="button"
                size={promoteInboundSales ? 'medium' : 'large'}
                fullWidth={true}
                className={stickyButton}
                onClick={handleStickyButtonClick}
                loading={submitting}
              >
                {t('BUTTON_LABEL_GET_PRICE')}
              </Button>
            </div>
          </ScrollPast>

          {error?.general && <p className={sprinkles({ textAlign: 'center' })}>{error.general}</p>}
        </div>
      </Card.Root>
    </form>
  )
}

function InboundSalesDialog() {
  const { t } = useTranslation(['common', 'purchase-form'])
  const { isOpen } = useInboundSalesOpen()

  if (!isOpen) {
    return null
  }

  // For product pages, we can pretty much hard the code the phone
  // number used since they will likely not change.
  const phone = '010-45 99 200'

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <Button
          className={stickyButton}
          // Edge case 'secondary-alt' styles. Hard to override via class
          style={{
            border: `1px solid ${tokens.colors.border.primary}`,
            backgroundColor: tokens.colors.surface.opaque.primary,
          }}
          type="button"
          size="medium"
          variant="secondary-alt"
          fullWidth={true}
        >
          {t('purchase-form:INBOUND_SALES_DIALOG_TRIGGER')}
        </Button>
      </Dialog.Trigger>

      <Dialog.Content frostedOverlay={true} centerContent={true} asChild={true} closeOnPointerDownOutside={true}>
        <Dialog.Window className={inboundSalesDialogWindow}>
          <header>
            <Dialog.Title asChild={true}>
              <Heading as="h1" variant="standard.18" color="textPrimary" align="center">
                {t('purchase-form:INBOUND_SALES_DIALOG_TITLE')}
              </Heading>
            </Dialog.Title>
            <Text as="p" size="md" color="textSecondary" align="center" balance={true}>
              {t('purchase-form:INBOUND_SALES_DIALOG_SUBTITLE')}
            </Text>
          </header>

          <div className={yStack({ gap: 'xs', mt: 'xl' })}>
            <Button as="a" href={`tel:${phone}`} variant="primary" size="large" fullWidth={true}>
              {t('purchase-form:INBOUND_SALES_DIALOG_CALL_CTA', { phone })}
            </Button>
            <Dialog.Close asChild={true}>
              <Button type="button" size="large" fullWidth={true} variant="secondary">
                {t('common:DIALOG_CLOSE')}
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Window>
      </Dialog.Content>
    </Dialog.Root>
  )
}
